/**
 * PDFJSTemplateViewerContainer - Container for PDF.js template viewer
 *
 * Responsibilities:
 * - Query template data (PDF URL, annotations, form fields)
 * - Handle template locking/unlocking
 * - Pass simplified props to PDFJSViewer (no counterparty, no mentions)
 * - Enforce organization template read-only mode
 */

import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { mutations } from '../../api';
import { useAuth } from '../../auth';
import { fillPlaceholders, paths } from '../../routing';
import { PDFJSViewer } from './PDFJSViewer';
import { useNotifications } from '../../notifications';
import LoadingSpinner from '../loading-spinner';
import { DocumentEditorErrors } from '../document-wizard/document-editor/constants/EditorConstats';
import { CounterpartyPermissionLevel } from '../../types/counterparty-permissions';
// Removed: useFormFields - PDFJSViewer now manages fields internally
import * as pdfjsLib from 'pdfjs-dist';

// GraphQL query for template data
const GET_TEMPLATE_FOR_PDFJS_VIEWER = gql`
  query GetTemplateForPDFJSViewer($documentTemplateId: ID!) {
    getDocumentTemplateById(documentTemplateId: $documentTemplateId) {
      id
      importedAssetUrl
      pdfFormFields
      pdfAnnotations
      updatedAt
      organizationName
    }
  }
`;

interface Props {
  templateId: string;
  teamId: string;
  hasEditPermission: boolean;
  hasCommentPermission: boolean;
  templateTitle: string;
  isOrganizationTemplate?: boolean;
  isSharedFromWorkspace?: boolean;
}

export const PDFJSTemplateViewerContainer: React.FC<Props> = ({
  templateId,
  teamId,
  hasEditPermission,
  hasCommentPermission,
  templateTitle,
  isOrganizationTemplate = false,
  isSharedFromWorkspace = false,
}) => {
  const [{ user }] = useAuth();
  const [, { hide: hideNotifications, error: notifyError }] = useNotifications();
  const navigate = useNavigate();
  const isLockedRef = useRef<boolean>(false);
  const [isTemplateLocked, setIsTemplateLocked] = useState(false);
  const [pdfDocument, setPdfDocument] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [lastSavedTimestamp, setLastSavedTimestamp] = useState<string | undefined>();
  const [pages, setPages] = useState<Array<{ id: string; position: number }>>([]);

  // Query template data
  const {
    data: templateData,
    loading: templateDataLoading,
    refetch: refetchTemplate,
  } = useQuery(GET_TEMPLATE_FOR_PDFJS_VIEWER, {
    variables: {
      documentTemplateId: templateId,
    },
    fetchPolicy: 'cache-first', // Use cache to prevent reload on refetch (matches DocumentPagePdfJs)
  });

  // Lock/unlock mutations
  const [lockDocumentTemplate, { loading: isLockTemplateLoading }] = useMutation(
    mutations.lockDocumentTemplate
  );
  const [unlockDocumentTemplate] = useMutation(mutations.unlockDocumentTemplate);

  // Save mutations
  const [saveTemplatePdfFormFields] = useMutation(mutations.saveTemplatePdfFormFields);
  const [saveTemplatePdfAnnotations] = useMutation(mutations.saveTemplatePdfAnnotations);

  const navigateToTemplatesPage = useCallback(() => {
    const url = fillPlaceholders(paths.templates.landing, { teamId });
    navigate(url);
  }, [teamId, navigate]);

  const handleUnlock = useCallback(() => {
    if (isLockedRef.current) {
      unlockDocumentTemplate({ variables: { documentId: templateId, userId: user.id } });
    }
  }, [unlockDocumentTemplate, templateId, user.id]);

  // Lock template on mount (unless organization template or shared from workspace)
  useEffect(() => {
    const isReadOnlyTemplate = isOrganizationTemplate || isSharedFromWorkspace;

    if (!isReadOnlyTemplate) {
      lockDocumentTemplate({ variables: { documentId: templateId, userId: user.id } })
        .then(() => {
          isLockedRef.current = true;
        })
        .catch((err: any) => {
          setIsTemplateLocked(true);
          notifyError({ message: DocumentEditorErrors.TEMPLATE_IS_LOCKED.defaultMessage, pinned: true });
        });
    }

    return () => {
      if (isLockedRef.current && !isReadOnlyTemplate) {
        unlockDocumentTemplate({ variables: { documentId: templateId, userId: user.id } });
      }
      hideNotifications();
    };
  }, [
    isOrganizationTemplate,
    isSharedFromWorkspace,
    lockDocumentTemplate,
    unlockDocumentTemplate,
    templateId,
    user.id,
    notifyError,
    hideNotifications,
  ]);

  // Unlock on page unload
  useEffect(() => {
    window.addEventListener('beforeunload', handleUnlock);

    return () => {
      window.removeEventListener('beforeunload', handleUnlock);
    };
  }, [handleUnlock]);

  // Parse saved data - handle both JSON strings and already-parsed arrays
  // Memoize based on actual data content (not object reference) to prevent unnecessary re-renders
  const template = templateData?.getDocumentTemplateById;
  const savedPdfFormFields = useMemo(() => {
    return template?.pdfFormFields
      ? Array.isArray(template.pdfFormFields)
        ? template.pdfFormFields
        : JSON.parse(template.pdfFormFields as string)
      : [];
  }, [JSON.stringify(template?.pdfFormFields)]);

  const savedPdfAnnotations = useMemo(() => {
    return template?.pdfAnnotations
      ? Array.isArray(template.pdfAnnotations)
        ? template.pdfAnnotations
        : JSON.parse(template.pdfAnnotations as string)
      : [];
  }, [JSON.stringify(template?.pdfAnnotations)]);

  // Fields are loaded from savedPdfFormFields prop and managed in PDFJSViewer's allFields state

  // Loading state (after all hooks)
  if (templateDataLoading || isLockTemplateLoading) {
    return <LoadingSpinner />;
  }

  if (!template) {
    return <div>Template not found</div>;
  }

  // Permissions: organization templates and shared workspace templates are always read-only
  const isReadOnlyTemplate = isOrganizationTemplate || isSharedFromWorkspace;
  const effectiveHasEditPermission = hasEditPermission && !isReadOnlyTemplate && !isTemplateLocked;
  const effectiveHasCommentPermission = hasCommentPermission && !isReadOnlyTemplate && !isTemplateLocked;

  // Determine permission level based on user's actual permissions
  const getEffectivePermissionLevel = (): CounterpartyPermissionLevel => {
    if (effectiveHasEditPermission) return CounterpartyPermissionLevel.FullEditing;
    if (effectiveHasCommentPermission) return CounterpartyPermissionLevel.CommentOnly;
    // No permissions - completely read-only (no comments, no editing)
    return CounterpartyPermissionLevel.CommentOnly; // Will be read-only via mode='read-only'
  };

  return (
    <PDFJSViewer
      pdfUrl={template.importedAssetUrl || ''}
      mode={effectiveHasEditPermission || effectiveHasCommentPermission ? 'edit' : 'read-only'}
      resourceType="template"
      documentId={templateId}
      teamId={teamId}
      documentTitle={templateTitle}
      lastSaved={lastSavedTimestamp || template.updatedAt}
      savedPdfFormFields={savedPdfFormFields}
      savedPdfAnnotations={savedPdfAnnotations}
      currentUserId={user.id.toString()}
      currentUserName={user.fullName}
      onDocumentLoad={setPdfDocument}
      onPagesLoad={setPages} // TWO-PHASE: Receive pages array updates
      onPdfSaveSuccess={(timestamp) => {
        // No refetch needed - Apollo automatically updates cache when mutations return id + fields
        // Capture timestamp from mutation response to update "Last saved" indicator
        if (timestamp) {
          setLastSavedTimestamp(timestamp);
        }
      }}
      showControls={true}
      showThumbnails={true}
      // Set permission level based on actual user permissions (not just edit)
      effectivePermissionLevel={getEffectivePermissionLevel()}
      // No counterparty permissions for templates (no permission dropdown)
      canAccessFormsToggle={effectiveHasEditPermission}
      canEditPermissions={false}
      hideComments={!effectiveHasCommentPermission && !effectiveHasEditPermission} // Hide comments if user has neither permission
    />
  );
};

export default PDFJSTemplateViewerContainer;
